import React, { useMemo, useState, Suspense } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Button
} from '@mui/material';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import GroupsIcon from '@mui/icons-material/Groups';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { isSameDay, format, isValid } from 'date-fns';
import { parseDate, formatDate } from '../../utils/dateUtils';
import { DEFAULT_TIME_BLOCKS } from '../../constants/competitionConstants';

const SchedulePrintPreview = React.lazy(() => import('../SchedulePrintPreview'));

const CompetitionSchedule = ({ sessions, gymnasts = [], competitionColors, startDate, endDate, competitionName, timezone, onSessionClick, rotations }) => {
  // Calculate actual gymnast count per session from gymnasts array
  const getSessionGymnastCount = (sessionNumber) => {
    return gymnasts.filter(g => Number(g.session) === Number(sessionNumber)).length;
  };
  // Format time to show time in HH:MM AM/PM format (without date)
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    // Parse the time string (which may be a full datetime like "2026-01-23 08:50:00")
    const parsedDate = new Date(dateString);
    if (!isValid(parsedDate)) return dateString; // Return original if parsing fails
    
    // Format as HH:MM AM/PM without the date portion
    return format(parsedDate, 'h:mm a');
  };
  
  // Helper to infer gender from rotation
  const getSessionGender = (session) => {
    if (session.gender) return session.gender;
    const rotation = rotations?.find(r => r.id === session.rotationId);
    if (rotation?.name?.toLowerCase().includes('men')) return 'M';
    if (rotation?.name?.toLowerCase().includes('women')) return 'F';
    return 'All';
  };

  // Export sessions to ICS calendar file
  const exportToICS = () => {
    const tz = timezone || 'America/New_York';

    // Helper to format date to ICS format (YYYYMMDDTHHMMSS)
    const formatICSDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (!isValid(date)) return null;
      return format(date, "yyyyMMdd'T'HHmmss");
    };

    // Escape special characters for ICS
    const escapeICS = (text) => {
      if (!text) return '';
      return text
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n');
    };

    // Generate unique ID for event
    const generateUID = (session) => {
      return `session-${session.sessionNumber}-${session.id || Date.now()}@virtius`;
    };

    // Build ICS content
    const events = sessions.map(session => {
      // Calendar event: use first available start time and last available end time
      // Start priority: competitionStart → marchInStart → warmUpStart → openStretchStart → startDate
      const startTime = session.timeBlocks?.competitionStart
        || session.timeBlocks?.marchInStart
        || session.timeBlocks?.warmUpStart
        || session.timeBlocks?.openStretchStart
        || session.startDate;
      // End priority: awardsEnd → competitionEnd → endDate
      const endTime = session.timeBlocks?.awardsEnd
        || session.timeBlocks?.competitionEnd
        || session.endDate;

      const dtStart = formatICSDate(startTime);
      const dtEnd = formatICSDate(endTime);

      if (!dtStart || !dtEnd) return null;

      // Build summary - include competition name
      const gender = getSessionGender(session);
      const genderLabel = gender === 'M' ? 'Men' : gender === 'F' ? 'Women' : '';
      const levels = session.levels?.join(', ') || '';
      const sessionInfo = `Session ${session.sessionNumber}${genderLabel ? ` - ${genderLabel}` : ''}${levels ? ` ${levels}` : ''}`;
      const summary = competitionName ? `${competitionName}: ${sessionInfo}` : sessionInfo;

      // Build description
      const descParts = [];
      if (session.type) descParts.push(`Format: ${session.type}`);
      if (session.numFlights > 1) descParts.push(`${session.numFlights} Flights`);
      const athleteCount = getSessionGymnastCount(session.sessionNumber);
      if (athleteCount > 0) descParts.push(`${athleteCount} Athletes`);
      if (session.description) descParts.push(session.description);

      // Add time breakdown - show which times are used for calendar event
      if (session.timeBlocks) {
        // Determine what's being used for start/end
        const startLabel = session.timeBlocks.competitionStart ? 'Competition Start'
          : session.timeBlocks.marchInStart ? 'March In'
          : session.timeBlocks.warmUpStart ? 'Warm Up'
          : session.timeBlocks.openStretchStart ? 'Open Stretch'
          : 'Start';
        const endLabel = session.timeBlocks.awardsEnd ? 'Awards End'
          : session.timeBlocks.competitionEnd ? 'Competition End'
          : 'End';

        descParts.push('');
        descParts.push(`CALENDAR EVENT: ${startLabel} → ${endLabel}`);
        descParts.push('');
        descParts.push('Full Schedule:');
        if (session.timeBlocks.openStretchStart) {
          const isStart = startLabel === 'Open Stretch';
          descParts.push(`  Open Stretch: ${formatTime(session.timeBlocks.openStretchStart)}${isStart ? ' ← Event Start' : ''}`);
        }
        if (session.timeBlocks.warmUpStart) {
          const isStart = startLabel === 'Warm Up';
          descParts.push(`  Warm Up: ${formatTime(session.timeBlocks.warmUpStart)}${isStart ? ' ← Event Start' : ''}`);
        }
        if (session.timeBlocks.marchInStart) {
          const isStart = startLabel === 'March In';
          descParts.push(`  March In: ${formatTime(session.timeBlocks.marchInStart)}${isStart ? ' ← Event Start' : ''}`);
        }
        if (session.timeBlocks.competitionStart) {
          const isStart = startLabel === 'Competition Start';
          descParts.push(`  Competition Start: ${formatTime(session.timeBlocks.competitionStart)}${isStart ? ' ← Event Start' : ''}`);
        }
        if (session.timeBlocks.competitionEnd) {
          const isEnd = endLabel === 'Competition End';
          descParts.push(`  Competition End: ${formatTime(session.timeBlocks.competitionEnd)}${isEnd ? ' ← Event End' : ''}`);
        }
        if (session.timeBlocks.awardsEnd) {
          const isEnd = endLabel === 'Awards End';
          descParts.push(`  Awards End: ${formatTime(session.timeBlocks.awardsEnd)}${isEnd ? ' ← Event End' : ''}`);
        }
      }

      const description = escapeICS(descParts.join('\\n'));
      const location = escapeICS(session.venue || '');

      return [
        'BEGIN:VEVENT',
        `UID:${generateUID(session)}`,
        `DTSTAMP:${format(new Date(), "yyyyMMdd'T'HHmmss'Z'")}`,
        `DTSTART;TZID=${tz}:${dtStart}`,
        `DTEND;TZID=${tz}:${dtEnd}`,
        `SUMMARY:${escapeICS(summary)}`,
        description ? `DESCRIPTION:${description}` : null,
        location ? `LOCATION:${location}` : null,
        'END:VEVENT'
      ].filter(Boolean).join('\r\n');
    }).filter(Boolean);

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Virtius//Competition Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      `X-WR-CALNAME:${escapeICS(competitionName || 'Competition Schedule')}`,
      `X-WR-TIMEZONE:${tz}`,
      ...events,
      'END:VCALENDAR'
    ].join('\r\n');

    // Create and download file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(competitionName || 'schedule').replace(/[^a-z0-9]/gi, '_')}_schedule.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Generate array of all dates in range
  const allDates = useMemo(() => {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    if (!start || !end) return [];

    const dates = [];
    let currentDate = start;
    while (currentDate <= end) {
      // Use the same format as sessionsByDay
      dates.push(formatDate(currentDate, 'MM/dd/yyyy'));
      currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
    }
    return dates;
  }, [startDate, endDate]);

  // Group sessions by day
  const sessionsByDay = useMemo(() => sessions.reduce((acc, session) => {
    const sessionDate = session.startDate
      ? parseDate(session.startDate)
      : session.timeBlocks?.competitionStart
        ? parseDate(session.timeBlocks.competitionStart)
        : null;

    if (sessionDate) {
      const date = formatDate(sessionDate, 'MM/dd/yyyy');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(session);
    }
    return acc;
  }, {}), [sessions]);

  const [viewMode, setViewMode] = useState('vertical');
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  return (
    <Box data-ui="schedule-content">
      {/* Schedule Header with styling matching other pages */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
        gap: 1
      }}
      data-ui="schedule-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">
            Competition Schedule
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Days:
            </Typography>
            <Chip
              label={allDates.length}
              size="small"
              sx={{
                backgroundColor: `${competitionColors.primary}22`,
                borderColor: competitionColors.primary
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sessions:
            </Typography>
            <Chip
              label={sessions.length}
              size="small"
              sx={{
                backgroundColor: `${competitionColors.primary}22`,
                borderColor: competitionColors.primary
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => setIsPrintPreviewOpen(true)}
            data-ui="schedule-print-button"
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 400,
              textTransform: 'none',
              color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={exportToICS}
            data-ui="schedule-export-ics-button"
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 400,
              textTransform: 'none',
              color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Export (.ics)
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="view mode"
            size="small"
            data-ui="schedule-view-toggle"
          >
            <ToggleButton value="vertical" aria-label="vertical view" data-ui="schedule-view-vertical">
              <ViewWeekIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="horizontal" aria-label="horizontal view" data-ui="schedule-view-horizontal">
              <ViewAgendaIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: viewMode === 'vertical' ? 'row' : 'column',
        gap: 2,
        overflowX: viewMode === 'vertical' ? 'auto' : 'visible',
        pb: viewMode === 'vertical' ? 2 : 0
      }}
      data-ui="schedule-days-container"
      >
        {allDates.map((date, index) => {
          // Calculate card width based on number of days
          const numDays = allDates.length;
          const getCardWidth = () => {
            if (viewMode === 'horizontal') return 'auto';
            if (numDays <= 3) {
              // For 3 or fewer days, distribute evenly with gaps
              const gapTotal = (numDays - 1) * 16; // 16px gap between cards
              return `calc((100% - ${gapTotal}px) / ${numDays})`;
            }
            // For 4+ days, show 3 cards with a hint of the 4th
            return 'calc(33.333% - 16px)';
          };
          
          const cardWidth = getCardWidth();
          
          return (
          <Card
            key={date}
            data-ui={`schedule-day-card-${index}`}
            sx={{
              mb: viewMode === 'horizontal' ? 3 : 0,
              boxShadow: 2,
              minWidth: viewMode === 'vertical' ? cardWidth : 'auto',
              maxWidth: viewMode === 'vertical' ? cardWidth : 'auto',
              flex: viewMode === 'vertical' ? `0 0 ${cardWidth}` : '1 1 auto'
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                backgroundColor: `${competitionColors.primary}22`,
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                {formatDate(parseDate(date), 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>
            <CardContent>
              {sessionsByDay[date]?.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: viewMode === 'vertical' ? 'column' : 'row',
                    gap: 1.5,
                    overflowX: viewMode === 'vertical' ? 'visible' : 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                      height: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 4,
                    },
                  }}
                >
                  {sessionsByDay[date].map((session) => (
                    <Box
                      key={session?.id || session?.sessionNumber}
                      sx={{
                        minWidth: viewMode === 'vertical' ? '100%' : 'calc(33.333% - 12px)',
                        flex: viewMode === 'vertical' ? '1 1 100%' : '0 0 calc(33.333% - 12px)',
                      }}
                    >
                      <Card
                        variant="outlined"
                        data-ui={`schedule-session-${session.sessionNumber}`}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 2,
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                        onClick={() => onSessionClick?.(session)}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          {/* Header Row: Title, Gender, Type */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.2 }}>
                                Session {session.sessionNumber}
                              </Typography>
                              <Chip
                                label={getSessionGender(session)}
                                size="small"
                                sx={{
                                  backgroundColor: `${competitionColors.primary}22`,
                                  borderColor: competitionColors.primary,
                                  height: '20px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                            <Chip
                              label={session.type
                                ? session.type
                                : (session.numFlights > 1 ? 'Capital Cup' : 'Traditional')
                              }
                              size="small"
                              sx={{
                                backgroundColor: `${competitionColors.secondary}22`,
                                borderColor: competitionColors.secondary,
                                height: '20px',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>

                          {/* Description if present */}
                          {session.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                              {session.description}
                            </Typography>
                          )}

                          {/* Compact Stats Row: Levels, Athletes, Flights */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
                            {session.levels?.length > 0 && (
                                session.levels.map((level, index) => (
                                  <Chip
                                    key={index}
                                    label={typeof level === 'string' && level.startsWith('Level ') ? level.substring(6) : level}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      backgroundColor: `${competitionColors.primary}11`,
                                      color: 'text.primary',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                ))
                            )}
                            
                            {(getSessionGymnastCount(session.sessionNumber) > 0 || session.numFlights > 1) && session.levels?.length > 0 && (
                              <Box component="span" sx={{ color: 'text.disabled', mx: 0.5 }}>|</Box>
                            )}

                            {getSessionGymnastCount(session.sessionNumber) > 0 && (
                              <Chip
                                icon={<GroupsIcon style={{ fontSize: 14 }} />}
                                label={getSessionGymnastCount(session.sessionNumber)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  borderColor: 'transparent',
                                  backgroundColor: 'transparent', 
                                  '& .MuiChip-icon': { ml: 0.5, mr: -0.5, color: 'text.secondary' },
                                  color: 'text.secondary',
                                  pl: 0
                                }}
                              />
                            )}

                            {session.numFlights > 1 && (
                              <Chip
                                label={`${session.numFlights} Flights`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  borderColor: 'transparent',
                                  backgroundColor: 'transparent',
                                  color: 'text.secondary',
                                  pl: 0
                                }}
                              />
                            )}
                          </Box>

                          {/* Schedule Blocks */}
                          {session.timeBlocks && (() => {
                            // Helper to parse time strings with reference date
                            const parseTimeString = (timeStr) => {
                              if (!timeStr) return null;

                              // Try parsing as full ISO date first
                              let date = new Date(timeStr);
                              if (!isNaN(date.getTime())) {
                                return date;
                              }

                              // Parse time-only strings like "8:40 AM" or "08:40"
                              const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                              if (timeMatch) {
                                let hours = parseInt(timeMatch[1]);
                                const minutes = parseInt(timeMatch[2]);
                                const isPM = timeMatch[3]?.toUpperCase() === 'PM';

                                // Convert to 24-hour format
                                if (isPM && hours !== 12) hours += 12;
                                if (!isPM && hours === 12) hours = 0;

                                // Create date with reference date (today) and parsed time
                                date = new Date();
                                date.setHours(hours, minutes, 0, 0);
                                return date;
                              }

                              return null;
                            };

                            // Only use canonical timeBlock keys to avoid duplicates (warmupStart vs warmUpStart)
                            const canonicalKeys = DEFAULT_TIME_BLOCKS.map(b => b.id);

                            const entries = Object.entries(session.timeBlocks)
                              .filter(([key, time]) => time && canonicalKeys.includes(key)) // Only canonical keys
                              .map(([name, time]) => {
                                const parsed = parseTimeString(time);
                                return { name, time, parsed, timestamp: parsed?.getTime() || 0 };
                              })
                              .filter(entry => entry.parsed !== null); // Remove invalid times

                            // Sort by time ascending
                            const sorted = entries.sort((a, b) => a.timestamp - b.timestamp);

                            const labelMap = {
                              openStretchStart: { text: 'Open Stretch', style: 'italic' },
                              warmUpStart: { text: 'Warm Up', style: 'italic' },
                              marchInStart: { text: 'March In', style: 'italic' },
                              competitionStart: { text: 'Start', style: 'bold' },
                              competitionEnd: { text: 'End', style: 'bold' },
                              awardsEnd: { text: 'Awards End', style: 'italic' },
                            };

                            if (sorted.length === 0) return null;

                            return (
                              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0 }}>
                                  {sorted.map(({ name: block, time }) => {
                                    const meta = labelMap[block] || {
                                      text: block.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase()),
                                      style: 'normal'
                                    };
                                    const isBold = meta.style === 'bold';
                                    const isItalic = meta.style === 'italic';
                                    return (
                                      <React.Fragment key={block}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontWeight: isBold ? 700 : 400, fontStyle: isItalic ? 'italic' : 'normal', lineHeight: 1.4 }}
                                        >
                                          {meta.text}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: isBold ? 700 : 400, textAlign: 'right', lineHeight: 1.4 }}
                                        >
                                          {formatTime(time)}
                                        </Typography>
                                      </React.Fragment>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })()}
                          {session.venue && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              @ {session.venue}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No sessions scheduled for this day
                </Typography>
              )}
            </CardContent>
          </Card>
          );
        })}
      </Box>

      {isPrintPreviewOpen && (
        <Suspense fallback={null}>
          <SchedulePrintPreview
            open={isPrintPreviewOpen}
            onClose={() => setIsPrintPreviewOpen(false)}
            sessions={sessions}
            gymnasts={gymnasts}
            competitionColors={competitionColors}
            startDate={startDate}
            endDate={endDate}
            rotations={rotations}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default CompetitionSchedule;
